/**
 * `useFeatures` — the React hook for feature flag access in the UI.
 *
 * The pure-function logic (which feature requires which, who can toggle
 * what, plan diffs) lives in `src/features/permissions.ts` so it can be
 * unit-tested without React or Supabase. The metadata constants live in
 * `useFeatures.constants.ts` for the same reason.
 *
 * This file is the **public surface** for the rest of the app:
 *
 *   - Existing imports `from '@/hooks/useFeatures'` keep working — every
 *     constant and type that used to live here is re-exported below.
 *   - New code can also import the typed React Query hook directly:
 *
 *       import { useFeatures } from '@/hooks/useFeatures';
 *       const { hasFeature, toggleFeature, settingType } = useFeatures();
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useDemo } from '@/contexts/DemoContext';
import { useSupportMode } from '@/contexts/SupportModeContext';
import { getDemoOrganization, DEMO_ORG_ID } from '@/lib/demoData';
import {
  canEnableFeature,
  getFeatureModulTyp,
  isFeatureToggleable,
  switchToPlanFeatures,
  toggleFeature as toggleFeatureList,
} from '@/features/permissions';
import {
  mergeNavOverride,
  parseNavOverrides,
  type NavOverrides,
} from '@/features/visibility';
import type { Visibility } from '@/features/types';
import {
  ALL_FEATURE_KEYS,
  DEFAULT_FEATURES,
  FEATURE_META,
  FEATURE_PLANS,
  FREIE_FUNKTIONEN,
  NAV_FEATURES,
  PATIENT_FEATURES,
  PUBLIC_MVP_DISABLED_FEATURES,
  type Feature,
  type FeatureMeta,
  type ModulTyp,
  type SettingType,
} from './useFeatures.constants';

// ── Re-exports for backwards compatibility ──────────────────────────────
//
// Lots of files import these directly via `from '@/hooks/useFeatures'`.
// We forward them so the constants split is invisible to the rest of
// the codebase.

export {
  ALL_FEATURE_KEYS,
  FEATURE_META,
  FEATURE_PLANS,
  FREIE_FUNKTIONEN,
  NAV_FEATURES,
  PATIENT_FEATURES,
  PUBLIC_MVP_DISABLED_FEATURES,
};
export type { Feature, FeatureMeta, ModulTyp, SettingType };

const PUBLIC_MVP_MODE =
  (import.meta.env.VITE_PUBLIC_MVP_MODE ?? 'true').toLowerCase() !== 'false';

function applyPublicMvpBoundary(features: readonly Feature[]): Feature[] {
  if (!PUBLIC_MVP_MODE) return [...features];
  const blocked = new Set<Feature>(PUBLIC_MVP_DISABLED_FEATURES);
  return features.filter((feature) => !blocked.has(feature));
}

// ── The hook ────────────────────────────────────────────────────────────

export const useFeatures = () => {
  const { profile } = useAuth();
  const { isDemo, demoSettingType } = useDemo();
  const { active: isSupportMode, session: supportSession } = useSupportMode();
  // Support-Modus überschreibt die eigene Org des Plattform-Admins durch
  // die Ziel-Org. Demo bleibt vorrangig — Demo-Modus ist eine eigene Welt.
  const orgId = isDemo
    ? DEMO_ORG_ID
    : isSupportMode && supportSession
      ? supportSession.targetOrgId
      : profile?.organization_id;

  const { data: org, isLoading: orgLoading } = useQuery({
    queryKey: ['org-features', orgId],
    enabled: !!orgId && !isDemo,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('organizations')
        .select('enabled_features, setting_type, nav_overrides, patient_tab_overrides')
        .eq('id', orgId!)
        .single();
      if (error) throw error;
      return data as {
        enabled_features: string[] | null;
        setting_type: SettingType | null;
        nav_overrides: unknown;
        patient_tab_overrides: unknown;
      };
    },
  });

  // Im Demo-Modus: alle Features aktiv, setting_type je nach Demo-Modus
  const demoOrg = isDemo
    ? (() => {
        const org = getDemoOrganization(demoSettingType ?? 'praxis');
        return {
          enabled_features: org.enabled_features,
          setting_type: org.setting_type as SettingType,
          nav_overrides: null as unknown,
          patient_tab_overrides: null as unknown,
        };
      })()
    : null;

  const effectiveOrg = isDemo ? demoOrg : org;

  const settingType: SettingType = effectiveOrg?.setting_type ?? 'praxis';

  const enabledFeatures: Feature[] = applyPublicMvpBoundary(
    effectiveOrg?.enabled_features != null
      ? (effectiveOrg.enabled_features as Feature[])
      : DEFAULT_FEATURES,
  );

  const navOverrides: NavOverrides | null = parseNavOverrides(effectiveOrg?.nav_overrides);
  const patientTabOverrides: NavOverrides | null = parseNavOverrides(
    effectiveOrg?.patient_tab_overrides,
  );

  const hasFeature = (feature: Feature) => enabledFeatures.includes(feature);

  // Pure-function delegations — see `src/features/permissions.ts`.
  const canEnable = (feature: Feature) => canEnableFeature(feature, enabledFeatures);
  const getModulTyp = (feature: Feature) => getFeatureModulTyp(feature);

  // Org admins (org_admin) and platform admins all have the "admin"
  // management surface — they can toggle their org's Freie Funktionen
  // and edit `nav_overrides`. Platform-tier actions (e.g. flipping the
  // paket itself) require `is_superadmin`, see `setPlan`.
  const isAdmin =
    profile?.role === 'admin' ||
    profile?.role === 'org_admin';
  const isToggleable = (feature: Feature) =>
    isFeatureToggleable(feature, { isAdmin, isSuperAdmin: profile?.is_superadmin ?? false });

  const queryClient = useQueryClient();

  const toggleMutation = useMutation({
    mutationFn: async (feature: Feature) => {
      if (isDemo) return; // Demo-Modus: kein DB-Zugriff
      if (!orgId) throw new Error('Keine Organisation');
      const next = toggleFeatureList(feature, enabledFeatures);
      const { error } = await supabase
        .from('organizations')
        .update({ enabled_features: next })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const toggleFeature = (feature: Feature) => {
    if (!isToggleable(feature)) return;
    if (!canEnable(feature) && !enabledFeatures.includes(feature)) return;
    toggleMutation.mutate(feature);
  };

  // ── Plan switch (Basis-Praxis ↔ Klinik) ────────────────────────────────
  //
  // Only superadmins can flip the paket: the basis/praxis/klinik tier
  // features are part of the customer's contract and are provisioned
  // by Icarus, while freie Funktionen are self-service for org admins.
  // `switchToPlanFeatures` preserves any add-ons that were already
  // enabled so the flip does not silently wipe the customer's extras.
  const isSuperadmin = Boolean(profile?.is_superadmin);

  const setPlanMutation = useMutation({
    mutationFn: async (plan: SettingType) => {
      if (isDemo) return;
      if (!orgId) throw new Error('Keine Organisation');
      const nextFeatures = switchToPlanFeatures(plan, enabledFeatures);
      const { error } = await supabase
        .from('organizations')
        .update({ setting_type: plan, enabled_features: nextFeatures })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const setPlan = (plan: SettingType) => {
    if (!isSuperadmin) return;
    if (plan === settingType) return; // no-op
    setPlanMutation.mutate(plan);
  };

  // ── Nav-override writes ────────────────────────────────────────────────
  //
  // Org admins manage UI chrome (`nav_overrides`) even though they
  // cannot toggle paid features. The override write is additive —
  // passing `null` for `visibility` removes a single entry; writing an
  // empty map clears the column entirely and falls back to code
  // defaults.
  const setNavOverrideMutation = useMutation({
    mutationFn: async (args: { routeId: string; visibility: Visibility | null }) => {
      if (isDemo) return;
      if (!orgId) throw new Error('Keine Organisation');
      const next = mergeNavOverride(navOverrides, args.routeId, args.visibility);
      // When every override has been cleared, persist `null` so the
      // DB column doesn't accumulate empty JSON objects over time.
      const payload = Object.keys(next).length > 0 ? next : null;
      const { error } = await supabase
        .from('organizations')
        .update({ nav_overrides: payload })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const resetNavOverridesMutation = useMutation({
    mutationFn: async () => {
      if (isDemo) return;
      if (!orgId) throw new Error('Keine Organisation');
      const { error } = await supabase
        .from('organizations')
        .update({ nav_overrides: null })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const setNavOverride = (routeId: string, visibility: Visibility | null) => {
    if (!isAdmin) return;
    setNavOverrideMutation.mutate({ routeId, visibility });
  };

  const resetNavOverrides = () => {
    if (!isAdmin) return;
    resetNavOverridesMutation.mutate();
  };

  // ── Patientenakte-Tab-Sichtbarkeits-Overrides ──────────────────────────
  //
  // Gleiches Muster wie Nav-Overrides, nur für die Patient-Tab-Registry
  // (`organizations.patient_tab_overrides`) — siehe
  // `src/features/patient-tab-registry.tsx`.
  const setPatientTabOverrideMutation = useMutation({
    mutationFn: async (args: { tabId: string; visibility: Visibility | null }) => {
      if (isDemo) return;
      if (!orgId) throw new Error('Keine Organisation');
      const next = mergeNavOverride(patientTabOverrides, args.tabId, args.visibility);
      const payload = Object.keys(next).length > 0 ? next : null;
      const { error } = await supabase
        .from('organizations')
        .update({ patient_tab_overrides: payload })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const resetPatientTabOverridesMutation = useMutation({
    mutationFn: async () => {
      if (isDemo) return;
      if (!orgId) throw new Error('Keine Organisation');
      const { error } = await supabase
        .from('organizations')
        .update({ patient_tab_overrides: null })
        .eq('id', orgId);
      if (error) throw error;
    },
    onSuccess: () => {
      if (!isDemo) {
        queryClient.invalidateQueries({ queryKey: ['org-features', orgId] });
      }
    },
  });

  const setPatientTabOverride = (tabId: string, visibility: Visibility | null) => {
    if (!isAdmin) return;
    setPatientTabOverrideMutation.mutate({ tabId, visibility });
  };

  const resetPatientTabOverrides = () => {
    if (!isAdmin) return;
    resetPatientTabOverridesMutation.mutate();
  };

  return {
    enabledFeatures,
    hasFeature,
    canEnable,
    orgId,
    isLoading: orgLoading,
    settingType,
    getModulTyp,
    isToggleable,
    toggleFeature,
    isTogglingFeature: toggleMutation.isPending,
    isAdmin,
    isSuperadmin,
    navOverrides,
    setPlan,
    isSettingPlan: setPlanMutation.isPending,
    setNavOverride,
    resetNavOverrides,
    isWritingNavOverride:
      setNavOverrideMutation.isPending || resetNavOverridesMutation.isPending,
    patientTabOverrides,
    setPatientTabOverride,
    resetPatientTabOverrides,
    isWritingPatientTabOverride:
      setPatientTabOverrideMutation.isPending || resetPatientTabOverridesMutation.isPending,
  };
};
